import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import type { Address } from 'viem';
import { useI18n } from '../../i18n/index.tsx';
import { fmtUsd, shortAddr } from '../../lib/format.ts';
import '../../styles/tree.css';

export interface TreeMember {
  address: Address;
  rank?: number;
  leftUsd?: bigint;
  rightUsd?: bigint;
  ownUsd?: bigint;
}
/** A place in the tree: a member, an open slot a new member can take, or nothing (below an open slot). */
export type TreeSlot = TreeMember | 'open' | 'none';

// Seven places in heap order: 0 the root, 1–2 its left and right, 3–6 the four below them.
const X = [50, 25, 75, 12.5, 37.5, 62.5, 87.5]; // % of the width
const Y = [54, 214, 352]; // px: the orb centres of each level
const R = [31, 24, 16]; // px: the orb radius of each level
const HEIGHT = 420; // with a third level
const SHORT = 300; // the root and its two places only
const level = (i: number) => (i === 0 ? 0 : i < 3 ? 1 : 2);
const side = (i: number) => (i === 0 ? 'root' : i === 1 || i === 3 || i === 4 ? 'left' : 'right');

/**
 * Two levels of the binary tree as a small 3D scene: members are lit orbs standing on a neon floor, blue on the left
 * team and pink on the right, joined to their parent by lines along which active stake flows up. Open slots are
 * glass orbs. Moving the pointer turns the scene a little; reduced motion keeps it still.
 */
export function Tree3D({ slots, viewer, rankName, onOpen }: {
  slots: TreeSlot[]; // 7, in heap order
  viewer: Address; // the connected wallet, shown as "You"
  rankName: (rank: number) => string;
  onOpen: (address: Address) => void;
}) {
  const { t } = useI18n();
  const box = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tilt = (e: PointerEvent<HTMLDivElement>) => {
    const s = stage.current;
    if (!s || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const r = e.currentTarget.getBoundingClientRect();
    const dx = (e.clientX - r.left) / r.width - 0.5;
    const dy = (e.clientY - r.top) / r.height - 0.5;
    s.style.setProperty('--ry', `${(dx * 10).toFixed(2)}deg`);
    s.style.setProperty('--rx', `${(8 - dy * 6).toFixed(2)}deg`);
  };
  const untilt = () => {
    stage.current?.style.removeProperty('--ry');
    stage.current?.style.removeProperty('--rx');
  };

  const px = (i: number) => (X[i] / 100) * width;
  const height = slots.slice(3).some((s) => s !== 'none') ? HEIGHT : SHORT;
  const root = slots[0] as TreeMember;
  const lines = [1, 2, 3, 4, 5, 6]
    .filter((c) => slots[c] !== 'none')
    .map((c) => {
      const p = (c - 1) >> 1;
      const lp = level(p);
      const dir = c % 2 === 1 ? -1 : 1;
      const sx = px(p) + dir * R[lp] * 0.82;
      const sy = Y[lp] + R[lp] * 0.55;
      const ex = px(c);
      const ey = Y[level(c)] - R[level(c)] - 4;
      const my = (sy + ey) / 2;
      return { c, d: `M ${sx} ${sy} C ${sx} ${my}, ${ex} ${my}, ${ex} ${ey}`, open: slots[c] === 'open' };
    });

  return (
    <div className="t3d-wrap">
      <div className="t3d" ref={box} style={{ height }} onPointerMove={tilt} onPointerLeave={untilt}>
        <div className="t3d-stage" ref={stage} key={root.address}>
          <div className="t3d-floor" aria-hidden />
          <svg className="t3d-lines" width={width} height={height} aria-hidden>
            <defs>
              <linearGradient id="t3d-left" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor="#38bdf8" />
                <stop offset="1" stopColor="#a78bfa" />
              </linearGradient>
              <linearGradient id="t3d-right" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor="#e879f9" />
                <stop offset="1" stopColor="#a78bfa" />
              </linearGradient>
            </defs>
            {lines.map(({ c, d, open }) => (
              <g key={c} className={`t3d-line ${side(c)}${open ? ' open' : ''}`}>
                <path d={d} className="base" stroke={`url(#t3d-${side(c)})`} />
                {open ? null : <path d={d} className="flow" />}
              </g>
            ))}
          </svg>
          {/* each team's active stake, on the branch into it */}
          {(['left', 'right'] as const).map((s, k) => (
            <div key={s} className={`t3d-leg ${s}`} style={{ left: `${X[k + 1]}%`, top: Y[1] - R[1] - 30 }} data-testid={`tree-leg-${s}`}>
              <span>{t(s === 'left' ? 'ref.legendLeft' : 'ref.legendRight')}</span>
              <b className="num">{fmtUsd((s === 'left' ? root.leftUsd : root.rightUsd) ?? 0n)}</b>
            </div>
          ))}
          {slots.map((slot, i) => (
            <TreeOrb key={i} i={i} slot={slot} you={slot !== 'open' && slot !== 'none' && slot.address === viewer} rankName={rankName} onOpen={onOpen} />
          ))}
        </div>
      </div>
      <div className="t3d-legend" aria-label="legend">
        <span><i className="dot left" />{t('ref.legendLeft')}</span>
        <span><i className="dot right" />{t('ref.legendRight')}</span>
        <span><i className="dot open" />{t('ref.openSlot')}</span>
        <span><i className="flow" />{t('ref.legendFlow')}</span>
      </div>
    </div>
  );
}

function TreeOrb({ i, slot, you, rankName, onOpen }: {
  i: number;
  slot: TreeSlot;
  you: boolean;
  rankName: (rank: number) => string;
  onOpen: (address: Address) => void;
}) {
  const { t } = useI18n();
  const lv = level(i);
  const style = { left: `${X[i]}%`, top: Y[lv], '--r': `${R[lv]}px`, '--i': i } as CSSProperties;
  const cls = `t3d-node l${lv} ${side(i)}`;
  if (slot === 'none') return null;
  if (slot === 'open')
    return (
      <div className={`${cls} open`} style={style}>
        <span className="orb" aria-hidden>+</span>
        {lv < 2 ? <span className="t3d-tag">{t('ref.openSlot')}</span> : null}
      </div>
    );
  const name = you ? t('ref.you') : shortAddr(slot.address);
  const body = (
    <>
      <span className="orb" aria-hidden />
      <span className="t3d-tag">
        <b className="num">{lv === 2 ? slot.address.slice(0, 6) : name}</b>
        {lv < 2 && slot.rank !== undefined ? <span className="rank">{rankName(slot.rank)}</span> : null}
        {lv === 1 && slot.leftUsd !== undefined ? (
          <span className="muted num">
            L {fmtUsd(slot.leftUsd)} · R {fmtUsd(slot.rightUsd ?? 0n)}
          </span>
        ) : null}
        {lv < 2 && slot.ownUsd !== undefined ? (
          <span className="muted num">
            {t('ref.own')} {fmtUsd(slot.ownUsd)}
          </span>
        ) : null}
      </span>
    </>
  );
  if (i === 0) return <div className={`${cls} member${you ? ' you' : ''}`} style={style} title={slot.address}>{body}</div>;
  return (
    <button type="button" className={`${cls} member`} style={style} title={slot.address} aria-label={`${name}: ${slot.address}`} onClick={() => onOpen(slot.address)}>
      {body}
    </button>
  );
}
